import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import boulderUrl from "../assets/environment/boulder_01.glb?url";
import boulderLodUrl from "../assets/environment/boulder_01_lod.glb?url";
import waterfrontGalleryUrl from "../assets/environment/cape_aster_waterfront_gallery.glb?url";
import roadBarrierUrl from "../assets/environment/concrete_road_barrier.glb?url";
import rockMossSetUrl from "../assets/environment/rock_moss_set_02_lod.glb?url";
import rockFaceOneUrl from "../assets/environment/rock_face_01.glb?url";
import rockFaceTwoUrl from "../assets/environment/rock_face_02.glb?url";
import shrubTwoUrl from "../assets/environment/shrub_02.glb?url";
import shrubThreeUrl from "../assets/environment/shrub_03.glb?url";
import {
  createSuntraceEnvironmentMaterialResponseCollection,
  type SuntraceEnvironmentMaterialResponseCollection,
  type SuntraceEnvironmentMaterialIdentity,
} from "./environmentMaterialResponse";
import type { SuntraceQuality } from "./types";

export const SUNTRACE_ROCK_MOSS_PART_CONTRACTS = {
  "rock-moss-10": {
    sourceNodeName: "rock_moss_set_02_rock10",
    triangles: 3_678,
    maximumDrawCalls: 1,
  },
  "rock-moss-11": {
    sourceNodeName: "rock_moss_set_02_rock11",
    triangles: 3_680,
    maximumDrawCalls: 1,
  },
  "rock-moss-13": {
    sourceNodeName: "rock_moss_set_02_rock13",
    triangles: 3_646,
    maximumDrawCalls: 1,
  },
} as const;

export type SuntraceRockMossPartId = keyof typeof SUNTRACE_ROCK_MOSS_PART_CONTRACTS;

export const SUNTRACE_ENVIRONMENT_ASSET_IDS = [
  "boulder",
  "boulder-lod",
  "cape-waterfront-gallery",
  "road-barrier",
  "rock-face-one",
  "rock-face-two",
  "shrub-two",
  "shrub-three",
  "rock-moss-10",
  "rock-moss-11",
  "rock-moss-13",
] as const;

export type SuntraceEnvironmentAssetId =
  (typeof SUNTRACE_ENVIRONMENT_ASSET_IDS)[number];

export type SuntraceEnvironmentAssets = {
  clone: (id: SuntraceEnvironmentAssetId) => THREE.Object3D;
  getTriangleCount: (id: SuntraceEnvironmentAssetId) => number;
  getWeatheringEnabled: () => boolean;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export type SuntraceEnvironmentAssetProgress = (progress: number) => void;

type EnvironmentAssetDefinition = {
  id: SuntraceEnvironmentAssetId;
  maximumTriangles: number;
  expectedTriangles?: number;
  maximumDrawCalls?: number;
  sourceNodeName?: string;
};

type EnvironmentAssetSourceDefinition = {
  url: string;
  assets: readonly EnvironmentAssetDefinition[];
};

export const SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS = {
  boulder: { maximumTriangles: 70_000 },
  "boulder-lod": {
    maximumTriangles: 4_500,
    expectedTriangles: 4_356,
    maximumDrawCalls: 1,
  },
  "cape-waterfront-gallery": { maximumTriangles: 18_000 },
  "road-barrier": { maximumTriangles: 40_000 },
  "rock-face-one": {
    maximumTriangles: 24_000,
    expectedTriangles: 20_174,
    maximumDrawCalls: 1,
  },
  "rock-face-two": {
    maximumTriangles: 34_000,
    expectedTriangles: 29_566,
    maximumDrawCalls: 1,
  },
  "shrub-two": {
    maximumTriangles: 58_000,
    expectedTriangles: 27_254,
    maximumDrawCalls: 1,
  },
  "shrub-three": {
    maximumTriangles: 22_000,
    expectedTriangles: 8_287,
    maximumDrawCalls: 1,
  },
  "rock-moss-10": {
    maximumTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-10"].triangles,
    expectedTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-10"].triangles,
    maximumDrawCalls: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-10"].maximumDrawCalls,
  },
  "rock-moss-11": {
    maximumTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-11"].triangles,
    expectedTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-11"].triangles,
    maximumDrawCalls: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-11"].maximumDrawCalls,
  },
  "rock-moss-13": {
    maximumTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-13"].triangles,
    expectedTriangles: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-13"].triangles,
    maximumDrawCalls: SUNTRACE_ROCK_MOSS_PART_CONTRACTS["rock-moss-13"].maximumDrawCalls,
  },
} as const satisfies Record<
  SuntraceEnvironmentAssetId,
  Omit<EnvironmentAssetDefinition, "id" | "sourceNodeName">
>;

const LOAD_TIMEOUT_MS = 20_000;
const MAXIMUM_TEXTURE_SIZE = 1_024;
const MAXIMUM_ASSET_EXTENT = 40;
const environmentMaterialIdentities: Record<
  SuntraceEnvironmentAssetId,
  SuntraceEnvironmentMaterialIdentity
> = {
  boulder: "rock",
  "boulder-lod": "rock",
  "cape-waterfront-gallery": "painted-metal",
  "road-barrier": "concrete",
  "rock-face-one": "rock",
  "rock-face-two": "rock",
  "shrub-two": "vegetation",
  "shrub-three": "vegetation",
  "rock-moss-10": "rock",
  "rock-moss-11": "rock",
  "rock-moss-13": "rock",
};
const assetSourceDefinitions: readonly EnvironmentAssetSourceDefinition[] = [
  {
    url: boulderUrl,
    assets: [{ id: "boulder", ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS.boulder }],
  },
  {
    url: boulderLodUrl,
    assets: [
      {
        id: "boulder-lod",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["boulder-lod"],
      },
    ],
  },
  {
    url: waterfrontGalleryUrl,
    assets: [
      {
        id: "cape-waterfront-gallery",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["cape-waterfront-gallery"],
      },
    ],
  },
  {
    url: roadBarrierUrl,
    assets: [
      {
        id: "road-barrier",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["road-barrier"],
      },
    ],
  },
  {
    url: rockFaceOneUrl,
    assets: [
      {
        id: "rock-face-one",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["rock-face-one"],
      },
    ],
  },
  {
    url: rockFaceTwoUrl,
    assets: [
      {
        id: "rock-face-two",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["rock-face-two"],
      },
    ],
  },
  {
    url: shrubTwoUrl,
    assets: [
      {
        id: "shrub-two",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["shrub-two"],
      },
    ],
  },
  {
    url: shrubThreeUrl,
    assets: [
      {
        id: "shrub-three",
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS["shrub-three"],
      },
    ],
  },
  {
    url: rockMossSetUrl,
    assets: Object.entries(SUNTRACE_ROCK_MOSS_PART_CONTRACTS).map(
      ([id, contract]) => ({
        id: id as SuntraceRockMossPartId,
        ...SUNTRACE_ENVIRONMENT_ASSET_CONTRACTS[id as SuntraceRockMossPartId],
        sourceNodeName: contract.sourceNodeName,
      }),
    ),
  },
];

type EnvironmentMesh = THREE.Mesh<
  THREE.BufferGeometry,
  THREE.Material | THREE.Material[]
>;

const isEnvironmentMesh = (object: THREE.Object3D): object is EnvironmentMesh =>
  object instanceof THREE.Mesh;

const isTexture = (value: unknown): value is THREE.Texture =>
  value instanceof THREE.Texture;

const getMaterialValues = (material: THREE.Material): unknown[] =>
  Object.values(material as unknown as Record<string, unknown>);

const getTriangleCount = (root: THREE.Object3D): number => {
  let triangleCount = 0;
  root.traverse((object) => {
    if (!isEnvironmentMesh(object)) {
      return;
    }
    const indexCount = object.geometry.index?.count;
    const positionCount = object.geometry.getAttribute("position").count;
    triangleCount += Math.floor((indexCount ?? positionCount) / 3);
  });
  return triangleCount;
};

const getMaterials = (mesh: EnvironmentMesh): THREE.Material[] =>
  Array.isArray(mesh.material) ? mesh.material : [mesh.material];

const isResponsiveEnvironmentMaterial = (
  material: THREE.Material,
): material is THREE.MeshStandardMaterial =>
  material instanceof THREE.MeshStandardMaterial ||
  material instanceof THREE.MeshPhysicalMaterial;

const shareSingleSidedLodMaterial = (
  source: THREE.Group,
  target: THREE.Group,
  sourceId: SuntraceEnvironmentAssetId,
  controllers: SuntraceEnvironmentMaterialResponseCollection,
): void => {
  const sourceMaterials = new Set<THREE.Material>();
  source.traverse((object) => {
    if (isEnvironmentMesh(object)) {
      for (const material of getMaterials(object)) {
        sourceMaterials.add(material);
      }
    }
  });
  const sourceMaterial = sourceMaterials.values().next().value;
  if (sourceMaterials.size !== 1 || sourceMaterial === undefined) {
    throw new Error(`${sourceId} must provide one shared LOD material`);
  }
  const material = sourceMaterial.clone();
  material.side = THREE.FrontSide;
  material.needsUpdate = true;
  if (isResponsiveEnvironmentMaterial(material)) {
    controllers.register(
      material,
      environmentMaterialIdentities[sourceId],
    );
  }
  const replacedMaterials = new Set<THREE.Material>();
  target.traverse((object) => {
    if (!isEnvironmentMesh(object)) {
      return;
    }
    for (const replacedMaterial of getMaterials(object)) {
      replacedMaterials.add(replacedMaterial);
    }
    object.material = material;
  });
  for (const replacedMaterial of replacedMaterials) {
    controllers.release(replacedMaterial);
    replacedMaterial.dispose();
  }
};

const getAmbientLift = (id: SuntraceEnvironmentAssetId): number => {
  if (id === "cape-waterfront-gallery") {
    return 0.045;
  }
  if (id === "shrub-two" || id === "shrub-three") {
    return 0.12;
  }
  if (id === "road-barrier") {
    return 0.055;
  }
  return 0.085;
};

const validateTexture = (texture: THREE.Texture): void => {
  const image = texture.image as { width?: number; height?: number } | undefined;
  const width = image?.width ?? 0;
  const height = image?.height ?? 0;
  if (width > MAXIMUM_TEXTURE_SIZE || height > MAXIMUM_TEXTURE_SIZE) {
    throw new Error(`Environment texture exceeds ${String(MAXIMUM_TEXTURE_SIZE)} pixels`);
  }
};

const configurePrototype = (
  definition: EnvironmentAssetDefinition,
  scene: THREE.Group,
  maximumAnisotropy: number,
  controllers: SuntraceEnvironmentMaterialResponseCollection,
): THREE.Group => {
  let meshCount = 0;
  const sourceMaterials = new Set<THREE.Material>();
  scene.traverse((object) => {
    if (!object.position.toArray().every(Number.isFinite)) {
      throw new Error(`${definition.id} has an invalid transform`);
    }
    if (!isEnvironmentMesh(object)) {
      return;
    }
    meshCount += 1;
    object.castShadow = true;
    object.receiveShadow = true;
    const materials = getMaterials(object).map((sourceMaterial) => {
      const material = isResponsiveEnvironmentMaterial(sourceMaterial)
        ? sourceMaterial.clone()
        : sourceMaterial;
      for (const value of getMaterialValues(material)) {
        if (isTexture(value)) {
          validateTexture(value);
          value.anisotropy = maximumAnisotropy;
        }
      }
      if (!isResponsiveEnvironmentMaterial(material)) {
        return material;
      }
      material.envMapIntensity = Math.max(material.envMapIntensity, 1.12);
      material.emissiveMap = material.map;
      material.emissive.setScalar(getAmbientLift(definition.id));
      material.emissiveIntensity = 1;
      if (definition.sourceNodeName !== undefined) {
        material.side = THREE.FrontSide;
      }
      material.needsUpdate = true;
      controllers.register(
        material,
        environmentMaterialIdentities[definition.id],
      );
      sourceMaterials.add(sourceMaterial);
      return material;
    });
    const material = materials[0];
    if (material === undefined) {
      throw new Error(`${definition.id} contains an empty material list`);
    }
    object.material = Array.isArray(object.material) ? materials : material;
  });
  for (const material of sourceMaterials) {
    material.dispose();
  }
  if (meshCount === 0) {
    throw new Error(`${definition.id} contains no meshes`);
  }
  scene.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(scene);
  const size = bounds.getSize(new THREE.Vector3());
  if (
    bounds.isEmpty() ||
    !size.toArray().every(Number.isFinite) ||
    Math.max(size.x, size.y, size.z) > MAXIMUM_ASSET_EXTENT
  ) {
    throw new Error(`${definition.id} has invalid bounds`);
  }
  const triangleCount = getTriangleCount(scene);
  if (triangleCount <= 0 || triangleCount > definition.maximumTriangles) {
    throw new Error(`${definition.id} exceeds its triangle budget`);
  }
  if (
    definition.expectedTriangles !== undefined &&
    triangleCount !== definition.expectedTriangles
  ) {
    throw new Error(`${definition.id} does not match its triangle contract`);
  }
  if (
    definition.maximumDrawCalls !== undefined &&
    meshCount > definition.maximumDrawCalls
  ) {
    throw new Error(`${definition.id} exceeds its draw-call contract`);
  }
  const center = bounds.getCenter(new THREE.Vector3());
  scene.position.x -= center.x;
  scene.position.y -= bounds.min.y;
  scene.position.z -= center.z;
  scene.name = definition.id;
  scene.updateMatrixWorld(true);
  return scene;
};

const selectAssetRoot = (
  sourceScene: THREE.Group,
  definition: EnvironmentAssetDefinition,
): THREE.Group => {
  if (definition.sourceNodeName === undefined) {
    return sourceScene;
  }
  const sourcePart = sourceScene.getObjectByName(definition.sourceNodeName);
  if (sourcePart === undefined) {
    throw new Error(
      `${definition.id} source node ${definition.sourceNodeName} is unavailable`,
    );
  }
  sourcePart.removeFromParent();
  const partRoot = new THREE.Group();
  partRoot.add(sourcePart);
  return partRoot;
};

const collectResources = (
  roots: Iterable<THREE.Object3D>,
): {
  geometries: Set<THREE.BufferGeometry>;
  materials: Set<THREE.Material>;
  textures: Set<THREE.Texture>;
} => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  for (const root of roots) {
    root.traverse((object) => {
      if (!isEnvironmentMesh(object)) {
        return;
      }
      geometries.add(object.geometry);
      for (const material of getMaterials(object)) {
        materials.add(material);
        for (const value of getMaterialValues(material)) {
          if (isTexture(value)) {
            textures.add(value);
          }
        }
      }
    });
  }
  return { geometries, materials, textures };
};

const disposeRoots = (roots: Iterable<THREE.Object3D>): void => {
  const rootList = [...roots];
  const resources = collectResources(rootList);
  for (const geometry of resources.geometries) {
    geometry.dispose();
  }
  for (const material of resources.materials) {
    material.dispose();
  }
  for (const texture of resources.textures) {
    texture.dispose();
  }
  for (const root of rootList) {
    root.clear();
  }
};

const disposeUnretainedRoots = (
  discardedRoots: Iterable<THREE.Object3D>,
  retainedRoots: Iterable<THREE.Object3D>,
): void => {
  const discardedRootList = [...discardedRoots];
  const retainedRootSet = new Set(retainedRoots);
  const discarded = collectResources(discardedRootList);
  const retained = collectResources(retainedRootSet);
  for (const geometry of discarded.geometries) {
    if (!retained.geometries.has(geometry)) {
      geometry.dispose();
    }
  }
  for (const material of discarded.materials) {
    if (!retained.materials.has(material)) {
      material.dispose();
    }
  }
  for (const texture of discarded.textures) {
    if (!retained.textures.has(texture)) {
      texture.dispose();
    }
  }
  for (const root of discardedRootList) {
    if (!retainedRootSet.has(root)) {
      root.clear();
    }
  }
};

export const loadSuntraceEnvironmentAssets = async (
  renderer: THREE.WebGLRenderer,
  signal?: AbortSignal,
  onProgress?: SuntraceEnvironmentAssetProgress,
): Promise<SuntraceEnvironmentAssets> => {
  if (signal?.aborted) {
    throw new DOMException("Environment loading was aborted", "AbortError");
  }
  let progress = 0;
  const loadState: { timedOut: boolean } = { timedOut: false };
  const reportProgress = (nextProgress: number): void => {
    progress = Math.max(progress, THREE.MathUtils.clamp(nextProgress, 0, 1));
    onProgress?.(progress);
  };
  const loadingManager = new THREE.LoadingManager();
  loadingManager.onProgress = (_url, loaded, total) => {
    reportProgress(total > 0 ? loaded / total : 0);
  };
  const loader = new GLTFLoader(loadingManager).setMeshoptDecoder(MeshoptDecoder);
  const materialControllers = createSuntraceEnvironmentMaterialResponseCollection();
  const abortLoading = (): void => {
    loadingManager.abort();
  };
  signal?.addEventListener("abort", abortLoading, { once: true });
  const timeout = window.setTimeout(() => {
    loadState.timedOut = true;
    loadingManager.abort();
  }, LOAD_TIMEOUT_MS);
  reportProgress(0);
  const maximumAnisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  const loadResults = await Promise.allSettled(
    assetSourceDefinitions.map(async (sourceDefinition) => {
      const gltf = await loader.loadAsync(sourceDefinition.url);
      const loadedAssets: {
        definition: EnvironmentAssetDefinition;
        scene: THREE.Group;
      }[] = [];
      try {
        for (const definition of sourceDefinition.assets) {
          const root = selectAssetRoot(gltf.scene, definition);
          loadedAssets.push({
            definition,
            scene: configurePrototype(
              definition,
              root,
              maximumAnisotropy,
              materialControllers,
            ),
          });
        }
        disposeUnretainedRoots(
          [gltf.scene],
          loadedAssets.map(({ scene }) => scene),
        );
        return loadedAssets;
      } catch (error) {
        disposeRoots([
          ...loadedAssets.map(({ scene }) => scene),
          gltf.scene,
        ]);
        throw error;
      }
    }),
  );
  window.clearTimeout(timeout);
  signal?.removeEventListener("abort", abortLoading);
  const fulfilled = loadResults
    .filter((result): result is PromiseFulfilledResult<{
      definition: EnvironmentAssetDefinition;
      scene: THREE.Group;
    }[]> => result.status === "fulfilled")
    .flatMap((result) => result.value);
  const rejected = loadResults.find(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (
    rejected !== undefined ||
    fulfilled.length !== SUNTRACE_ENVIRONMENT_ASSET_IDS.length
  ) {
    materialControllers.dispose();
    disposeRoots(fulfilled.map(({ scene }) => scene));
    if (signal?.aborted) {
      throw new DOMException("Environment loading was aborted", "AbortError");
    }
    if (loadState.timedOut) {
      throw new Error("Environment loading timed out");
    }
    const detail = rejected?.reason instanceof Error
      ? rejected.reason.message
      : "Unknown asset error";
    throw new Error(`Environment assets could not be loaded: ${detail}`);
  }
  const boulder = fulfilled.find(
    ({ definition }) => definition.id === "boulder",
  )?.scene;
  const boulderLod = fulfilled.find(
    ({ definition }) => definition.id === "boulder-lod",
  )?.scene;
  if (
    boulder === undefined ||
    boulderLod === undefined
  ) {
    materialControllers.dispose();
    disposeRoots(fulfilled.map(({ scene }) => scene));
    throw new Error("Environment LOD material source is unavailable");
  }
  shareSingleSidedLodMaterial(
    boulder,
    boulderLod,
    "boulder",
    materialControllers,
  );
  const prototypes = new Map<SuntraceEnvironmentAssetId, THREE.Group>();
  const triangleCounts = new Map<SuntraceEnvironmentAssetId, number>();
  for (const { definition, scene } of fulfilled) {
    prototypes.set(definition.id, scene);
    triangleCounts.set(definition.id, getTriangleCount(scene));
  }
  let disposed = false;
  reportProgress(1);
  return {
    clone(id) {
      if (disposed) {
        throw new Error("Environment assets are disposed");
      }
      const prototype = prototypes.get(id);
      if (prototype === undefined) {
        throw new Error(`Environment asset ${id} is unavailable`);
      }
      return prototype.clone(true);
    },
    getTriangleCount(id) {
      return triangleCounts.get(id) ?? 0;
    },
    getWeatheringEnabled() {
      return materialControllers.weatheringEnabled;
    },
    setQuality(quality) {
      materialControllers.setQuality(quality);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      materialControllers.dispose();
      disposeRoots(prototypes.values());
      prototypes.clear();
      triangleCounts.clear();
    },
  };
};
